import { type Address, getAddress } from 'viem';
import type { StateCreator } from 'zustand';
import type { AuthConnectRequest, AuthConnectResponse, AuthPayloadResponse } from '@/api/spec.ts';
import { telemetry } from '../../utils/telemetry';
import { type RootState, resetUserStores } from '../rootStore.ts';

export type AuthMethod = 'email' | 'wallet' | null;

export type AuthStatus =
  | 'UNAUTHORIZED' // Initial state
  | 'AUTHORITY_BLOCKED' // User not allowed to use site by local laws
  | 'WALLET_CONNECTED' // Wallet connected but not signed, can get validation payload now
  | 'AUTHENTICATED' // User agrees with terms, backend validated signature, JWT received
  | 'AUTHORIZED' // User fully authorized with exchange account
  | 'RESTRICTED'; // User restricted from accessing the exchange

const AT_LEAST_CONNECTED_STATUSES: AuthStatus[] = ['WALLET_CONNECTED', 'AUTHENTICATED', 'AUTHORIZED'];
const AT_LEAST_AUTHENTICATED_STATUSES: AuthStatus[] = ['AUTHENTICATED', 'AUTHORIZED'];

export interface TermsAcceptanceState {
  mandatory: boolean;
  tournamentDialogSeen?: boolean;
}

/** Where an authority block caught the user, for the onboarding funnel's `failed` event. */
export interface AuthorityBlockContext {
  step: 'connect' | 'auth';
  country?: string;
}

export interface AuthNonce {
  value: string;
  address: Address;
  chainId: number;
  expiresAt?: number;
}

export interface AuthSlice {
  status: AuthStatus;
  authMethod: AuthMethod;
  address?: Address;
  inviteCode?: string;
  authPayload?: AuthPayloadResponse; // to be cleared after use
  authNonce?: AuthNonce; // should be stored for WS connection
  referredBy?: string;
  termsAccepted: Record<string, TermsAcceptanceState>; // store terms acceptance per wallet
  hasShownWikImage: boolean; // track if WikImage has been shown in current auth session
  summerBannerSeen: boolean; // Purple Summer banner dismissed on this device; persisted, survives logout
  millionBannerSeen: boolean; // like summerBannerSeen
  pendingAccessRequest?: {
    signedRequest: Omit<AuthConnectRequest, 'ref_code'>;
    errorStatus: number;
  };

  // Action methods
  connectWallet: (method: 'email' | 'wallet', address: Address, chainId: number) => void;
  setAuthPayload: (payload?: AuthPayloadResponse) => void;
  saveInviteCode: (accessCode: string) => void;
  authenticate: (data: AuthConnectResponse, address: Address, chainId: number) => void;
  setReferredBy: (referredBy: string) => void;
  setTermsAcceptance: (address: Address, mandatory: boolean) => void;
  setPendingAccessRequest: (request?: { signedRequest: Omit<AuthConnectRequest, 'ref_code'>; errorStatus: number }) => void;

  setLeaderboardDialogSeen: () => void;
  authorize: () => void;
  blockByAuthority: (context?: AuthorityBlockContext) => void;
  setRestricted: () => void;
  logout: () => void;
  setWikImageShown: () => void;
  setSummerBannerSeen: () => void;
  setMillionBannerSeen: () => void;

  // Reset methods
  resetToWalletConnected: () => void;

  // Helper methods for checking status
  isAtLeastConnected: () => boolean;
  isAtLeastAuthenticated: () => boolean;
  isFullyAuthorized: () => boolean;
}

// Canonical validity check for the auth nonce.
export function getValidNonce(nonce: AuthNonce | undefined): AuthNonce | undefined {
  if (!nonce) return undefined;
  if (nonce.expiresAt != null && nonce.expiresAt <= Date.now()) return undefined;
  return nonce;
}

export const createAuthSlice: StateCreator<RootState, [['zustand/immer', never]], [], AuthSlice> = (set, get) => ({
  status: 'UNAUTHORIZED',
  authMethod: null,
  address: undefined,
  authPayload: undefined,
  authNonce: undefined,
  termsAccepted: {},
  hasShownWikImage: false,
  summerBannerSeen: false,
  millionBannerSeen: false,
  pendingAccessRequest: undefined,

  // Flow
  connectWallet: (method, address, chainId) =>
    set((state) => {
      if (state.status === 'AUTHORITY_BLOCKED') {
        return;
      }
      if (state.status === 'UNAUTHORIZED') {
        const nonce = state.authNonce;
        const sameWallet = nonce?.address === address && nonce?.chainId === chainId;
        // Trust unknown-expiry nonces (legacy persisted state from older app versions).
        // Backend will reject if actually expired and the normal re-auth flow takes over.
        const notLocallyExpired = nonce?.expiresAt == null || nonce.expiresAt > Date.now();
        if (sameWallet && notLocallyExpired && state.termsAccepted[address]?.mandatory) {
          state.status = 'AUTHENTICATED';
        } else {
          state.status = 'WALLET_CONNECTED';
          state.authNonce = undefined;
          state.referredBy = undefined; // belongs to the previous wallet; /auth/connect repopulates it
        }
        state.authMethod = method;
        state.address = address;
      }
    }),

  setAuthPayload: (payload) => {
    set((state) => {
      state.authPayload = payload;
    });
  },

  saveInviteCode: (accessCode) =>
    set((state) => {
      state.inviteCode = accessCode;
    }),

  authenticate: (data, address, chainId) =>
    set((state) => {
      if (state.status === 'WALLET_CONNECTED' && state.address) {
        state.status = 'AUTHENTICATED';
        state.authNonce = {
          value: data.nonce,
          address: getAddress(address),
          chainId,
          expiresAt: data.expires_at,
        };
        state.authPayload = undefined;
        state.pendingAccessRequest = undefined;
        state.referredBy = data.referrer; // absent referrer means the profile has none
      }
    }),

  setReferredBy: (referredBy) =>
    set((state) => {
      state.referredBy = referredBy;
    }),

  setTermsAcceptance: (address, mandatory) =>
    set((state) => {
      const existing = state.termsAccepted[address];
      state.termsAccepted[address] = {
        mandatory,
        tournamentDialogSeen: existing?.tournamentDialogSeen,
      };
    }),

  setPendingAccessRequest: (request) =>
    set((state) => {
      state.pendingAccessRequest = request;
    }),

  setLeaderboardDialogSeen: () =>
    set((state) => {
      if (state.address) {
        const existing = state.termsAccepted[state.address];
        state.termsAccepted[state.address] = {
          mandatory: existing?.mandatory ?? false,
          tournamentDialogSeen: true,
        };
      }
    }),

  authorize: () =>
    set((state) => {
      if (state.status === 'AUTHENTICATED') {
        state.status = 'AUTHORIZED';
      }
    }),

  blockByAuthority: (context) => {
    // Logged here, ahead of the reset, because reset() rotates PostHog's distinct_id: a failure
    // reported after it lands on a fresh anonymous person that can never be joined to the funnel
    // entrant. Every caller was doing this the wrong way round.
    telemetry.logOnboardingFailureOnce('authority_block', {
      step: context?.step ?? 'connect',
      reason: 'geo_blocked',
      country: context?.country ?? null,
    });
    telemetry.reset();
    resetUserStores();
    set((state) => {
      state.status = 'AUTHORITY_BLOCKED';
      state.authMethod = null;
      state.address = undefined;
      state.authPayload = undefined;
      state.authNonce = undefined;
      state.inviteCode = undefined;
      state.referredBy = undefined;
      state.hasShownWikImage = false;
      state.pendingAccessRequest = undefined;
    });
  },

  setRestricted: () =>
    set((state) => {
      state.status = 'RESTRICTED';
      state.authPayload = undefined;
      state.authNonce = undefined;
    }),

  setWikImageShown: () =>
    set((state) => {
      state.hasShownWikImage = true;
    }),

  setSummerBannerSeen: () =>
    set((state) => {
      state.summerBannerSeen = true;
    }),

  setMillionBannerSeen: () =>
    set((state) => {
      state.millionBannerSeen = true;
    }),

  logout: () => {
    telemetry.reset();
    resetUserStores();
    set((state) => {
      state.status = 'UNAUTHORIZED';
      state.authMethod = null;
      state.address = undefined;
      state.authPayload = undefined;
      state.authNonce = undefined;
      state.inviteCode = undefined;
      state.referredBy = undefined;
      state.hasShownWikImage = false;
      state.pendingAccessRequest = undefined;
    });
  },

  // Reset methods for error handling
  // Only reset if we're beyond some state
  resetToWalletConnected: () =>
    set((state) => {
      if (AT_LEAST_CONNECTED_STATUSES.includes(state.status)) {
        state.status = 'WALLET_CONNECTED';
        state.authNonce = undefined;
        state.authPayload = undefined;
        // The next sign-in gets a fresh snapshot; until then /portfolio must not read the old account as known.
        state.walletSnapshotReceived = false;
      }
    }),

  // Helpers
  isAtLeastConnected: () => {
    const status = get().status;
    return AT_LEAST_CONNECTED_STATUSES.includes(status);
  },

  isAtLeastAuthenticated: () => {
    const status = get().status;
    return AT_LEAST_AUTHENTICATED_STATUSES.includes(status);
  },

  isFullyAuthorized: () => get().status === 'AUTHORIZED',
});

// Watches authNonce.expiresAt and demotes auth state at the expiry moment.
// Re-checks on visibilitychange because setTimeout drifts in backgrounded tabs.
type AuthExpirySupervisorStore = {
  getState: () => Pick<AuthSlice, 'authNonce' | 'resetToWalletConnected'>;
  subscribe: (listener: (state: { authNonce?: AuthNonce }, prev: { authNonce?: AuthNonce }) => void) => () => void;
};

// setTimeout truncates to Int32 — delays beyond ~24.8 days fire immediately.
// Tick in chunks and re-check remaining time on each chunk.
const SETTIMEOUT_MAX_MS = 2_147_483_647;
// Defensive ceiling: never trust a server-issued expiresAt longer than this.
// A stale nonce sitting in localStorage forever is worse than forcing a re-sign.
const MAX_NONCE_TTL_MS = 6 * 7 * 24 * 60 * 60 * 1_000;

export function installAuthExpirySupervisor(store: AuthExpirySupervisorStore): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const schedule = () => {
    clearTimeout(timer);
    const nonce = store.getState().authNonce;
    if (!nonce?.expiresAt) return;

    const deadline = Math.min(nonce.expiresAt, Date.now() + MAX_NONCE_TTL_MS);
    const tick = () => {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        timer = undefined;
        store.getState().resetToWalletConnected();
        return;
      }
      timer = setTimeout(tick, Math.min(remaining, SETTIMEOUT_MAX_MS));
    };
    tick();
  };

  const unsubscribe = store.subscribe((state, prev) => {
    if (state.authNonce !== prev.authNonce) {
      schedule();
    }
  });

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      schedule();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  schedule();

  return () => {
    clearTimeout(timer);
    unsubscribe();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };
}
